import Button from "components/shared/Button"
import Loading from "components/shared/Loading"
import { Select } from "components/shared/panda/Select"
import { useRouter } from "next/router"
import { useTranslation } from "next-i18next"
import { ChangeEvent, useCallback, useMemo, useState } from "react"
import { Form } from "schemas"
import { styled } from "styled-system/jsx"
import * as z from "zod"

const s = {}

type Props = {
  fields?: Form["fields"]
}

function CustomFields({ fields }: Props) {
  if (!fields) return null

  return (
    <>
      {fields?.map(({ _key, ...field }) =>
        field._type === "textarea" ? (
          <styled.textarea
            key={_key}
            placeholder={field.label}
            className={s.input}
            css={{
              p: "4px 16px",
              height: "45px",
              alignSelf: "stretch",
              borderRadius: "4px",
              border: "1px solid #404EDB",
              background: "#E3E7FF",
            }}
            name={field.hubspotId}
            cols={30}
            rows={3}></styled.textarea>
        ) : field._type === "select" ? (
          <styled.div
            key={_key}
            css={{
              width: "100%",
              color: "#000",
            }}>
            <styled.div css={{ width: "100%" }}>
              <Select
                css={{
                  width: "100%",
                  padding: "0 1rem",
                  appearance: "revert",
                  border: "none",
                  alignSelf: "stretch",
                }}
                id={`${_key}x`}
                name={field.hubspotId}
                defaultValue="">
                <option value="" disabled>
                  {field.label || "-"}
                </option>

                {field.options.map(option => (
                  <option key={option._key} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </styled.div>
          </styled.div>
        ) : field._type === "audioInput" ? (
          <FileInput variant="primary-light" name="avatar" accept="audio/*">
            {field.label}
          </FileInput>
        ) : null
      )}
    </>
  )
}

const MAX_FILE_SIZE = 5_000_000

const baseSchema =
  typeof window === "undefined"
    ? z.undefined()
    : z
        .any()
        .optional()
        .refine(files => files?.length === 1, "fileRequired")
        .refine(files => files?.[0]?.type.startsWith("audio/"), "fileAudioType")

const schemaWithSizeLimits =
  typeof window === "undefined"
    ? z.undefined()
    : baseSchema
        .refine(files => files?.[0]?.size <= MAX_FILE_SIZE, "fileSize")
        .refine(async files => {
          const duration = await getDuration(files)
          return duration > 0 && duration <= 60 * 5
        }, "fileDuration")

const nvSecret = process.env.NEXT_PUBLIC_NV_SECRET
export const useFileSchema = () => {
  const { query } = useRouter()

  return useMemo(() => {
    if (nvSecret && query.no_validate === nvSecret) return baseSchema
    else return schemaWithSizeLimits
  }, [query.no_validate])
}

export const getDuration = (rawFiles: FileList): Promise<number> => {
  return new Promise((resolve, reject) => {
    if (!rawFiles) return resolve(0)

    const files = Array.from(rawFiles)

    if (!files[0]) return resolve(0)

    const audio = document.createElement("audio")
    const reader = new FileReader()

    reader.addEventListener("load", e => {
      audio.src = e.target.result
      audio.addEventListener(
        "loadedmetadata",
        () => resolve(audio.duration),
        false
      )
    })

    reader.readAsDataURL(files[0])
    setTimeout(() => resolve(0), 10_000)
  })
}

export const FileInput = ({
  name,
  variant = "primary-light",
  children,
  accept,
}) => {
  const { t } = useTranslation("common", { keyPrefix: "validation" })
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState("")
  const [error, setError] = useState<any>()
  const fileSchema = useFileSchema()

  const handleChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      setLoading(true)
      setError(null)
      setFileName("")

      const result = await fileSchema.safeParseAsync(e.target.files)

      if (!result.success) setError(result.error)
      else setFileName((result.data as FileList)[0].name)

      setLoading(false)
    },
    [fileSchema]
  )

  return (
    <div
      style={{
        display: "flex",
        gap: "0.5em 1em",
        alignItems: "center",
        flexWrap: "wrap",
      }}>
      <Button variant={variant} as="label" htmlFor={name} disabled={loading}>
        {children}
        <input
          onChange={handleChange}
          style={{ display: "none" }}
          type="file"
          id={name}
          name={name}
          accept={accept}
        />
      </Button>
      {loading ? (
        <Loading />
      ) : error?.issues?.[0]?.message ? (
        // <p>{error.issues[0]?.message}</p>
        <p style={{ color: "red" }}>
          {error?.issues
            ?.map(x => t(x.message, { defaultValue: x.message }))
            .join(". ")}
        </p>
      ) : (
        fileName
      )}
    </div>
  )
}

export default CustomFields
export type { Props as CustomFieldsProps }
